import type React from 'react';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createContainer,
	createHook,
	createStore,
	type Action,
} from '@atlassian/react-sweet-state';
import { createLocalStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/local-storage/index.tsx';

interface PopoutState {
	lastVisitedSearch: string | null;
	content: React.ReactNode;
}

const LOCAL_STORAGE_KEY_LAST_VISITED_SEARCH = 'jira-ops-alert-filters-last-visited-search';
const localStorageProvider = createLocalStorageProvider('alert_filters_popout');

const setAlertFiltersLastVisitedInLocalStorage = (value: PopoutState['lastVisitedSearch']) => {
	try {
		localStorageProvider.set(LOCAL_STORAGE_KEY_LAST_VISITED_SEARCH, value);
	} catch (_) {
		return false;
	}
	return true;
};
const getAlertFiltersLastVisitedFromLocalStorage = (): string | undefined => {
	let item;
	try {
		item = localStorageProvider.get(LOCAL_STORAGE_KEY_LAST_VISITED_SEARCH);
	} catch (_) {
		return undefined;
	}
	return item;
};

const getOnInit = () => {
	return {
		lastVisitedSearch: getAlertFiltersLastVisitedFromLocalStorage(),
	};
};

const actions = {
	setLastVisitedSearch:
		(value: string | null): Action<PopoutState> =>
		({ getState, setState }) => {
			if (getState().lastVisitedSearch) {
				setState({ ...getState(), lastVisitedSearch: value });
				setAlertFiltersLastVisitedInLocalStorage(value);
			}
		},
	togglePoppedOut:
		(value: string): Action<PopoutState> =>
		({ getState, setState }) => {
			if (!getState().lastVisitedSearch) {
				setState({ ...getState(), lastVisitedSearch: value });
				setAlertFiltersLastVisitedInLocalStorage(value);
			} else {
				setState({ ...getState(), lastVisitedSearch: null });
				setAlertFiltersLastVisitedInLocalStorage(null);
			}
		},
	syncLastVisitedSearch:
		(): Action<PopoutState> =>
		({ getState, setState }) => {
			setState({ ...getState(), lastVisitedSearch: getAlertFiltersLastVisitedFromLocalStorage() });
		},
	setContent:
		(content: React.ReactNode): Action<PopoutState> =>
		({ getState, setState }) => {
			setState({ ...getState(), content });
		},
};

const Store = createStore<PopoutState, typeof actions>({
	initialState: {
		content: null,
		lastVisitedSearch: ' ',
	},
	actions,
	name: 'AlertsPopoutStore',
});
export const AlertsPopoutStoreContainer = createContainer(Store, {
	onInit:
		() =>
		({ setState }) => {
			setState(getOnInit());
		},
});

const useHook = createHook(Store);
export const useAlertsPopoutStore = () => {
	const [state, hookActions] = useHook();
	return {
		state,
		...hookActions,
	};
};
